import { stringify } from 'query-string'
import { useCallback, useEffect, useRef, useState } from 'react'
import { StreamEventType, ToolCall } from 'src/agent/types/streamingTypes'
import {
  StreamInterruptBatch,
  StreamInterruptDecision,
} from 'src/queries/agent/network/agent.models'
import { getRequestConfig, RequestConfig } from 'src/queries/requestConfig'
import { ExtendThreadIDHeader } from 'src/queries/requestConfig.models'

export interface StreamMessage {
  id: string
  content?: string
  timestamp: Date
  type:
    | 'thought'
    | 'tool'
    | 'tool_start'
    | 'result'
    | 'error'
    | 'done'
    | 'interrupt'
  eventType?: StreamEventType
  toolCalls?: ToolCall[]
  interruptBatch?: StreamInterruptBatch
}

export interface UseStreamingAgentChatOptions {
  threadId?: string
  onMessage?: (message: StreamMessage) => void
  onError?: (error: Error) => void
  onComplete?: () => void
  onInterrupt?: (batch: StreamInterruptBatch) => void
  context?: {
    organizationId?: string
    transactionId?: string
  }
}

export interface UseStreamingAgentChatReturn {
  messages: StreamMessage[]
  isConnected: boolean
  isStreaming: boolean
  error: Error | null
  sendPrompt: (prompt: string) => Promise<void>
  resumeAgent: (decisions: StreamInterruptDecision[]) => Promise<void>
  clearMessages: () => void
  threadId: string | null
  pendingInterrupt: StreamInterruptBatch | null
}

/**
 * Hook for streaming agent chat using Server-Sent Events (SSE)
 * Connects to /api/v1/agent endpoint in muppeteer service
 */
export const useStreamingExpensePolicyAgentChat = (
  options: UseStreamingAgentChatOptions = {},
): UseStreamingAgentChatReturn => {
  const [messages, setMessages] = useState<StreamMessage[]>([])
  const [isConnected, setIsConnected] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [threadId, setThreadId] = useState<string | null>(
    options.threadId || null,
  )
  const [pendingInterrupt, setPendingInterrupt] =
    useState<StreamInterruptBatch | null>(null)

  const eventSourceRef = useRef<EventSource | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  // Use ref to track the latest threadId value to avoid stale closures
  const threadIdRef = useRef<string | null>(threadId)

  const addMessage = useCallback(
    (
      content: string | undefined,
      type: StreamMessage['type'],
      eventType?: StreamEventType,
      toolCalls?: ToolCall[],
      interruptBatch?: StreamInterruptBatch,
    ) => {
      const message: StreamMessage = {
        id: `${Date.now()}-${Math.random()}`,
        content,
        timestamp: new Date(),
        type,
        eventType,
        toolCalls,
        interruptBatch,
      }
      setMessages((prev) => [...prev, message])
      options.onMessage?.(message)
      return message
    },
    [options],
  )

  const clearMessages = useCallback(() => {
    setMessages([])
    setError(null)
  }, [])

  const closeConnection = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    setIsConnected(false)
    setIsStreaming(false)
  }, [])

  /**
   * Process SSE stream from response body
   * Shared between sendPrompt and resumeAgent
   */
  const processSSEStream = useCallback(
    async (response: Response) => {
      // Extract thread ID from response headers
      const responseThreadId = response.headers.get(ExtendThreadIDHeader)
      if (responseThreadId && responseThreadId !== threadIdRef.current) {
        setThreadId(responseThreadId)
        threadIdRef.current = responseThreadId
      }

      // Read SSE stream from response body
      const reader = response.body?.getReader()
      const decoder = new TextDecoder()

      if (!reader) {
        throw new Error('Response body is not readable')
      }

      setIsConnected(true)

      let currentEvent = ''
      let buffer = ''

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read()

        if (done) {
          break
        }

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')

        // Keep the last incomplete line in the buffer
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            // Parse event type
            currentEvent = line.slice(7).trim()
          } else if (line.startsWith('data: ')) {
            // Parse data
            const rawData = line.slice(6).trim()

            if (!rawData) continue

            // Handle based on event type
            switch (currentEvent) {
              case 'tool_start_event': {
                try {
                  // Parse tool call data
                  const parsed = JSON.parse(rawData)
                  const toolCalls = parsed.tool_calls || []
                  addMessage(
                    undefined,
                    'tool_start',
                    StreamEventType.TOOL_START,
                    toolCalls,
                  )
                } catch {
                  // Fallback to raw data if parsing fails
                  addMessage(rawData, 'tool_start')
                }
                break
              }

              case 'ai_event':
              case 'tool_event': {
                try {
                  // Parse JSON response with markdown content
                  const parsed = JSON.parse(rawData)
                  const content = parsed.markdown || rawData
                  const eventType =
                    currentEvent === 'ai_event'
                      ? StreamEventType.AI_EVENT
                      : StreamEventType.TOOL_EVENT
                  addMessage(
                    content,
                    currentEvent === 'ai_event' ? 'thought' : 'tool',
                    eventType,
                  )
                } catch {
                  // Fallback to raw data if not JSON
                  addMessage(
                    rawData,
                    currentEvent === 'ai_event' ? 'thought' : 'tool',
                  )
                }
                break
              }

              case 'interrupt_event': {
                try {
                  // Parse interrupt batch data
                  const interruptBatch: StreamInterruptBatch =
                    JSON.parse(rawData)
                  setPendingInterrupt(interruptBatch)
                  // Add message to show interrupt in chat
                  const interruptMessage =
                    interruptBatch.interrupts.length === 1
                      ? interruptBatch.interrupts[0].confirmation.message
                      : `${interruptBatch.interrupts.length} actions require your approval`
                  addMessage(
                    interruptMessage,
                    'interrupt',
                    StreamEventType.INTERRUPT_EVENT,
                    undefined,
                    interruptBatch,
                  )
                  options.onInterrupt?.(interruptBatch)
                  // Pause streaming while waiting for user decision
                  setIsStreaming(false)
                } catch {
                  // Show user-friendly error instead of raw data
                  addMessage(
                    'Failed to process approval request. Please try again.',
                    'error',
                  )
                }
                break
              }

              case 'done':
                addMessage('Completed', 'done', StreamEventType.DONE)
                options.onComplete?.()
                setIsStreaming(false)
                break

              case 'error': {
                const err = new Error(rawData)
                setError(err)
                addMessage(rawData, 'error', StreamEventType.ERROR)
                options.onError?.(err)
                break
              }

              default:
                // Fallback for messages without event type
                try {
                  const parsed = JSON.parse(rawData)
                  const content = parsed.markdown || rawData
                  addMessage(content, 'thought')
                } catch {
                  addMessage(rawData, 'thought')
                }
            }
          }
        }

        currentEvent = ''
      }

      setIsStreaming(false)
      setIsConnected(false)
    },
    [addMessage, options],
  )

  const sendPrompt = useCallback(
    async (prompt: string) => {
      try {
        setError(null)
        setIsStreaming(true)
        setPendingInterrupt(null)

        // Get fresh auth config at call time
        const config = getRequestConfig(RequestConfig.Agent)

        // Build SSE URL (baseURL already includes /api/v1)
        const url = new URL(
          `${config.baseURL}/expense-policy-agent?${stringify(options.context || {})}`,
          window.location.origin,
        )

        // Create abort controller for this request
        abortControllerRef.current = new AbortController()

        // Make POST request to initiate SSE stream
        const response = await fetch(url.toString(), {
          method: 'POST',
          headers: {
            ...(config.headers as Record<string, string>),
            'Content-Type': 'application/json',
            ...(threadIdRef.current
              ? { [ExtendThreadIDHeader]: threadIdRef.current }
              : {}),
          },
          body: JSON.stringify({ prompt }),
          signal: abortControllerRef.current.signal,
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        await processSSEStream(response)
      } catch (err) {
        if (err instanceof Error) {
          if (err.name === 'AbortError') {
            // Request was cancelled, not an error
            return
          }
          setError(err)
          addMessage(err.message, 'error')
          options.onError?.(err)
        }
        setIsStreaming(false)
        setIsConnected(false)
      }
    },
    [addMessage, options, processSSEStream],
  )

  /**
   * Resume agent after interrupt resolution
   * Sends decisions to POST /api/v1/agent/resume
   */
  const resumeAgent = useCallback(
    async (decisions: StreamInterruptDecision[]) => {
      if (!threadIdRef.current) {
        const err = new Error('Cannot resume: no thread ID available')
        setError(err)
        options.onError?.(err)
        return
      }

      // Validate decisions match pending interrupt count
      if (
        pendingInterrupt &&
        decisions.length !== pendingInterrupt.interrupts.length
      ) {
        const err = new Error(
          `Decision count (${decisions.length}) does not match interrupt count (${pendingInterrupt.interrupts.length})`,
        )
        setError(err)
        options.onError?.(err)
        return
      }

      try {
        setError(null)
        setIsStreaming(true)
        setPendingInterrupt(null)

        // Get fresh auth config at call time
        const config = getRequestConfig(RequestConfig.Agent)

        // Build resume URL (baseURL already includes /api/v1)
        const url = new URL(
          `${config.baseURL}/expense-policy-agent/resume`,
          window.location.origin,
        )

        // Create abort controller for this request
        abortControllerRef.current = new AbortController()

        // Make POST request to resume agent with decisions
        const response = await fetch(url.toString(), {
          method: 'POST',
          headers: {
            ...(config.headers as Record<string, string>),
            'Content-Type': 'application/json',
            [ExtendThreadIDHeader]: threadIdRef.current,
          },
          body: JSON.stringify({ decisions }),
          signal: abortControllerRef.current.signal,
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        // Process the SSE stream from resume response
        await processSSEStream(response)
      } catch (err) {
        if (err instanceof Error) {
          if (err.name === 'AbortError') {
            // Request was cancelled, not an error
            return
          }
          setError(err)
          addMessage(err.message, 'error')
          options.onError?.(err)
        }
        setIsStreaming(false)
        setIsConnected(false)
      }
    },
    [addMessage, options, pendingInterrupt, processSSEStream],
  )

  // Sync threadId ref with state
  useEffect(() => {
    threadIdRef.current = threadId
  }, [threadId])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      closeConnection()
    }
  }, [closeConnection])

  return {
    messages,
    isConnected,
    isStreaming,
    error,
    sendPrompt,
    resumeAgent,
    clearMessages,
    threadId,
    pendingInterrupt,
  }
}
